import { getStage } from "@/pages/Jobs/job-stages";
import { Badge } from "@/shared";
import { BadgeProps } from "@/shared/Badge";
import { getAppType } from "@/utils";
import { getStatusConfigs } from "@/utils/status-configs";

export interface StatusBadgeProps extends BadgeProps {
  status: string | undefined;
  label?: string;
  text?: string;
  component?: "job" | "leg" | "item";
}

const normalizeKey = (value?: string) =>
  String(value || "")
    .toLowerCase()
    .replace(/[_\s-]/g, "");

const getStageFromConfigs = (
  status?: string,
  component: "job" | "leg" | "item" = "job",
  type: string = getAppType(),
) => {
  if (!status) return null;

  const configs = getStatusConfigs();
  const bucket =
    component === "leg"
      ? configs?.legStages
      : component === "item"
        ? configs?.jobItemStages
        : configs?.jobStages;

  const stages = bucket?.[type] || bucket?.serviceRepair;
  if (!stages) return null;

  const normalized = normalizeKey(status);
  const match = (Object.values(stages) as any[]).find((stage: any) => {
    return (
      normalizeKey(stage?.id) === normalized ||
      normalizeKey(stage?.title) === normalized
    );
  });

  return match || null;
};

export const getStageColor = (
  key: string,
  type = getAppType(),
  component: "job" | "leg" | "item" = "job",
): BadgeProps["color"] => {
  const stage =
    getStageFromConfigs(key, component, type) || getStage(key, type);
  return stage.color || "primary";
};

export const getBadgeColor = (
  status?: string | undefined,
  component: "job" | "leg" | "item" = "job",
): BadgeProps["color"] => {
  if (!status) return "primary";
  return getStageColor(status, getAppType(), component);
};

const formatStatusLabel = (value?: string) => {
  if (!value) return "";
  const withSpaces = value
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

  return withSpaces
    .split(" ")
    .map((word) =>
      word
        ? `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`
        : "",
    )
    .join(" ");
};

const getStatusLabel = (
  status?: string,
  label?: string,
  text?: string,
  component: "job" | "leg" | "item" = "job",
) => {
  const source = text || label;
  if (source) {
    return formatStatusLabel(source);
  }

  if (status) {
    const stage =
      getStageFromConfigs(status, component, getAppType()) ||
      getStage(status, getAppType());
    if (stage?.title) {
      return stage.title;
    }
  }

  return formatStatusLabel(status);
};

export const StatusBadge = ({
  status,
  label,
  text,
  component = "job",
  color,
  children,
  ...props
}: StatusBadgeProps) => {
  const displayText = getStatusLabel(status, label, text, component);
  const badgeColor = getBadgeColor(status, component);

  return (
    <Badge color={badgeColor} label={displayText} {...props}>
      {!displayText ? children : null}
    </Badge>
  );
};
